/**
 * invoiceEngineConfig.ts — config silnika formatek dla obu klas faktur
 * (keystone unifikacja-formatek, Krok 2).
 *
 * `purchaseInvoiceEngineConfig` + `saleInvoiceEngineConfig`: KAŻDE pole core
 * faktury dostaje custom renderer, którym jest ISTNIEJĄCY renderer z rejestru
 * `PURCHASE_INVOICE_CORE_RENDERERS` / `SALE_INVOICE_CORE_RENDERERS` (reuse
 * werbatim — zero przepisania widgetów). Adapter tłumaczy kontekst silnika
 * `FieldRenderContext<TForm>` na kontrakt istniejących rendererów
 * (`set`/`setMany`/`error`/`companyId`), więc zachowane są 1:1:
 *  - testid'y `pi-field-*` / `si-field-*`,
 *  - DictionarySelect (`document_type`/`urgency`/`payment_method` — zakup),
 *  - enum statusu z `correction` (SelectInput, NIE słownik, NIE shared StatusSelect),
 *  - picker kontrahenta (vendor_id → setMany, atomowa denormalizacja nazwy/NIP),
 *  - podświetlenie pól wymaganych (error → hasError; clear-on-change → clearError).
 *
 * `document_type_id` NIE jest polem seeda faktur (potwierdzone) — zostaje w
 * stanie + badge nagłówka, poza silnikiem (żadnego wpisu w `fields`).
 *
 * `labelSource: 'definition'` — etykiety z definicji pól (field.name), tak jak
 * dotąd renderery faktur (`p.field.name`). `coerceEmptyToNull: false` — renderery
 * faktur robią własną koercję (`v || null`), silnik nie delegatuje żadnego pola
 * natywnie (wszystkie mają renderer), więc gałąź koercji i tak jest martwa.
 */

import type { ReactNode } from 'react';

import { documentCompanyFieldConfig } from '@/components/documents/documentCompanyRenderer';
import {
  type ProcessReadonlyExtra,
  renderReadonlyProcessField,
} from '@/components/invoices/processReadonlyRenderer';
import type { CoreFieldMeta } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';
import type { EngineFormField } from '@/components/kartoteki/engine/engine-types';
import { PURCHASE_INVOICE_CORE_RENDERERS } from '@/components/purchase-invoices/purchaseInvoiceCoreRenderers';
import {
  EMPTY_PURCHASE_INVOICE_FORM,
  type PurchaseInvoiceFormData,
} from '@/components/purchase-invoices/purchaseInvoiceFormTypes';
import { SALE_INVOICE_CORE_RENDERERS } from '@/components/sale-invoices/saleInvoiceCoreRenderers';
import {
  EMPTY_SALE_INVOICE_FORM,
  type SaleInvoiceFormData,
} from '@/components/sale-invoices/saleInvoiceFormTypes';
import { getComputedProcessFields } from '@/lib/processes/computed-fields';

/**
 * Stan runtime specyficzny dla faktur, niesiony przez `FieldRenderContext.extra` /
 * `KartotekaRenderRuntime.extra` (silnik go nie interpretuje — patrz engine-config.ts).
 */
export interface InvoiceExtra extends ProcessReadonlyExtra {
  /** Dokument ma pozycje → kwoty nagłówka wyliczane serwerowo, pola kwot read-only. */
  hasItems: boolean;
}

/**
 * Kontrakt istniejących rendererów faktur (wspólny kształt Purchase/Sale, tylko
 * różny TForm). Renderer dostaje meta pola + helpery zapisu/walidacji.
 */
type InvoiceRenderer<TForm> = (props: {
  field: CoreFieldMeta;
  form: TForm;
  set: <K extends keyof TForm>(key: K, value: TForm[K]) => void;
  setMany: (patch: Partial<TForm>) => void;
  error: (key: keyof TForm) => boolean;
  errorText: (key: keyof TForm) => string | undefined;
  editable: boolean;
  isCreate: boolean;
  /** Dokument ma pozycje → kwoty nagłówka wyliczane serwerowo, pola kwot read-only. */
  hasItems?: boolean;
  companyId?: string | null;
}) => ReactNode;

/**
 * Ground-truth `fieldType` per slug (z seeda). Używany WYŁĄCZNIE przez fallback
 * statyczny silnika (403 na metadanych modułu) do zbudowania EngineFormField —
 * renderer faktury i tak owns widget, więc wartość jest kosmetyczna, ale trzymamy
 * ją zgodną z seedem. Slug spoza mapy → 'varchar' (defensywnie).
 */
const INVOICE_FIELD_TYPES: Record<string, string> = {
  system_number: 'varchar',
  invoice_number: 'varchar',
  ksef_number: 'varchar',
  // Ewidencja KSeF/JPK (0159)
  ksef_issue_date: 'date',
  purchase_evidence_marker: 'select',
  jpk_document_marker: 'select',
  goods_import: 'boolean',
  ksef_issue_mode: 'select',
  document_receipt_channel: 'select',
  ksef_submitted_at: 'timestamp',
  ksef_available_at: 'timestamp',
  ksef_source_ip: 'varchar',
  document_type: 'select',
  status: 'select',
  urgency: 'select',
  corrected_invoice_id: 'reference',
  issue_date: 'date',
  received_date: 'date',
  sale_date: 'date',
  payment_due_date: 'date',
  vendor_id: 'reference',
  vendor_tax_id_cached: 'varchar',
  vendor_name_cached: 'varchar',
  currency_code: 'varchar',
  exchange_rate: 'numeric',
  amount_net: 'numeric',
  amount_vat: 'numeric',
  amount_gross: 'numeric',
  payment_method: 'select',
  paid_at: 'date',
  vendor_bank_account_id: 'reference',
  payment_notes: 'text',
  description: 'text',
  cost_center_default: 'varchar',
  rejection_reason: 'text',
  notes: 'text',
  owner_user_id: 'reference',
  // Kategoria dokumentu (oś `record_categories`, kartoteka_key='documents') — mirror letters.
  category_id: 'select',
  // T21 finding CRITICAL — dosypka wspólnych pól dokumentu (DOCUMENT_COMMON_CORE_FIELDS).
  source: 'select',
  external_id: 'varchar',
  external_source: 'varchar',
};

interface BuildInvoiceConfigArgs<TForm> {
  moduleSlug: string;
  testIdPrefix: string;
  renderers: Record<string, InvoiceRenderer<TForm>>;
  /** Pusty formularz — guard `isFormKey` w `setMany` (parytet z DataTab.setMany). */
  emptyForm: TForm;
}

/**
 * Buduje config silnika z rejestru rendererów faktur. Klucze rejestru (w
 * kolejności seeda) = `coreFieldSlugs` (synteza zakładki 'data' przez top-up
 * gdy brak layoutu) ORAZ mapa `fields` (każdy slug → adapter istniejącego renderera).
 */
function buildInvoiceEngineConfig<TForm>({
  moduleSlug,
  testIdPrefix,
  renderers,
  emptyForm,
}: BuildInvoiceConfigArgs<TForm>): KartotekaEngineConfig<TForm, InvoiceExtra> {
  // `coreFieldSlugs` DERYWOWANE z kluczy rejestru rendererów — slug nie zniknie po cichu
  // (usunięcie renderera złamałoby build/render). Dlatego faktury NIE mają dedykowanego
  // test-strażnika obecności kategorii na formularzu (jak klasy z ręczną listą slugów).
  const slugs = Object.keys(renderers);
  const fields: Record<string, CoreFieldConfig<TForm, InvoiceExtra>> = {};

  for (const slug of slugs) {
    const existing = renderers[slug]!;
    fields[slug] = {
      fieldType: INVOICE_FIELD_TYPES[slug] ?? 'varchar',
      renderer: (ctx) =>
        existing({
          field: {
            slug: ctx.field.slug,
            name: ctx.field.name,
            // Pole daty numeracji typu → wymagane (gwiazdka): numer nie powstanie bez tej
            // daty. Wstrzykiwane W ADAPTERZE, żeby nie tykać każdego renderera osobno —
            // renderery czytają `field.isRequired` tylko do `<Field required>`, więc zapala
            // się WYŁĄCZNIE pole o pasującym slugu (pola kwot nigdy nie pasują).
            isRequired: ctx.field.isRequired || ctx.isFieldExtraRequired(ctx.field),
          },
          form: ctx.form,
          // `set` z rejestru: zapis pojedynczego pola + wyczyszczenie jego błędu
          // walidacji (parytet z DataTab: `onChange` + `onClearError?.(key)`).
          set: <K extends keyof TForm>(key: K, value: TForm[K]) => {
            // Computed-key partial z generycznego K — konwersja przez unknown
            // (nie `any`), standardowy escape hatch dla `{ [k]: v }` → Partial<TForm>.
            ctx.setField({ [key]: value } as unknown as Partial<TForm>);
            ctx.clearError?.(key);
          },
          // `setMany`: atomowy zapis wielu pól (vendor picker) + wyczyszczenie
          // błędów zmienianych kluczy. Guard `isFormKey` (klucz obecny w
          // emptyForm) — dosłowny parytet z DataTab.setMany: clearError tylko dla
          // realnych kluczy formularza, nigdy dla obcego klucza w patchu.
          setMany: (patch: Partial<TForm>) => {
            ctx.setField(patch);
            const clear = ctx.clearError;
            if (clear) {
              for (const key of Object.keys(patch)) {
                if (key in (emptyForm as Record<string, unknown>)) clear(key as keyof TForm);
              }
            }
          },
          error: (key: keyof TForm) => ctx.hasError?.(key) ?? false,
          errorText: (key: keyof TForm) => ctx.getError?.(key),
          editable: ctx.editable,
          isCreate: ctx.isCreate,
          hasItems: ctx.extra?.hasItems ?? false,
          companyId: ctx.companyId,
        }),
    };
  }
  // Firma dokumentu — tylko do odczytu, opt-in z układu (NIE w coreFieldSlugs): patrz documentCompanyRenderer.tsx.
  fields.company_id = documentCompanyFieldConfig<TForm, InvoiceExtra>();

  // Pola WYLICZANE procesu (aktywny proces / bieżący etap) — readonly, spoza katalogu
  // system_module_fields (ADR-0093). Rejestr w kodzie (computed-fields.ts) → renderer
  // readonly (bez inputu) + wpis w `virtualFields` (żeby silnik rozpoznał slug po
  // przeciągnięciu na layout). NIE dodajemy do `coreFieldSlugs` — pola są opt-in (admin je
  // sam przeciąga z palety), nie pojawiają się w syntezowanym layoucie automatycznie.
  const computedProcessFields = getComputedProcessFields(moduleSlug);
  const virtualFields: EngineFormField[] = computedProcessFields.map((cf) => ({
    id: cf.slug,
    slug: cf.slug,
    // Etykieta realna pochodzi z renderera (t(labelKey)); `name` nie jest renderowane dla tych pól.
    name: cf.slug,
    fieldType: cf.fieldType,
    description: null,
    isCore: true,
    isRequired: false,
    options: null,
    gcrecord: null,
  }));
  for (const cf of computedProcessFields) {
    fields[cf.slug] = {
      fieldType: cf.fieldType,
      renderer: (ctx) =>
        renderReadonlyProcessField({
          slug: cf.slug,
          labelKey: cf.labelKey,
          extra: ctx.extra,
          testIdPrefix: ctx.testIdPrefix,
          t: ctx.t,
        }),
    };
  }

  return {
    moduleSlug,
    testIdPrefix,
    coreFieldSlugs: slugs,
    fields,
    virtualFields,
    labelSource: 'definition',
    coerceEmptyToNull: false,
    // Sloty nazwanych paneli (Symfonia / KSeF status+UPO) — deklarowane, ale
    // PUSTE: realne panele i ich pozycjonowanie na layoucie (element panel_view
    // z panelKey + rejestracja klocka w palecie buildera) należą do R4.
    // Do tego czasu żaden panel_view nie referuje tych kluczy — slot jest uśpiony.
    panels: {},
  };
}

export const purchaseInvoiceEngineConfig: KartotekaEngineConfig<
  PurchaseInvoiceFormData,
  InvoiceExtra
> = buildInvoiceEngineConfig<PurchaseInvoiceFormData>({
  moduleSlug: 'purchase_invoices',
  testIdPrefix: 'pi',
  renderers: PURCHASE_INVOICE_CORE_RENDERERS,
  emptyForm: EMPTY_PURCHASE_INVOICE_FORM,
});

export const saleInvoiceEngineConfig: KartotekaEngineConfig<SaleInvoiceFormData, InvoiceExtra> =
  buildInvoiceEngineConfig<SaleInvoiceFormData>({
    moduleSlug: 'sale_invoices',
    testIdPrefix: 'si',
    renderers: SALE_INVOICE_CORE_RENDERERS,
    emptyForm: EMPTY_SALE_INVOICE_FORM,
  });
